import { useEffect, useRef } from "react";

import { useAppDispatch, useTypedSelector } from "store/reducers/use-typed-selector";
import { Session } from "types/working-model";
import { addToDummyChat, setDummyChat } from "store/actions/event/chat-actions";
import { DummyChat } from "./dummy-chat";
import { sleep } from "utils/utils";


const useSetDummyChat = ({ session }: { session: Session | null }) => {
	const eventId = useTypedSelector(state => state.CreateEventReducer.workingEvent?.event);
	const channelId = useTypedSelector(state => state.CreateEventReducer.workingEvent?.channel);

	const dispatch = useAppDispatch();

	const timer = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (!session) return;
		if (!eventId) return;
		if (!channelId) return;

		const singleCycleOfComments = DummyChat({
			channelId,
			eventId,
			sessionId: session.session,
			language: session.default_language || 'en'
		});

		// use the same list 4 times, but we can't have the same id's, so we increment them
		const comments = [...singleCycleOfComments];
		let newId = singleCycleOfComments[comments.length - 1].id;
		for (let i = singleCycleOfComments.length; i < singleCycleOfComments.length * 3; i++) {
			newId++;
			comments.push({
				...singleCycleOfComments[i % singleCycleOfComments.length],
				id: newId,
			});
		}

		let time = 500;
		let hasRanAtleastOnce = false;

		const recursiveFeed = (idx: number) => {
			if (timer.current) clearTimeout(timer.current);
			if (idx === 0 && hasRanAtleastOnce) {
				// add a slight delay when restarting to allow the fadout out animation to do its thing
				time = 1500;
			} else {
				time = 500;
			}
			hasRanAtleastOnce = true;

			timer.current = setTimeout(async () => {
				const comment = comments?.[idx];
				if (comment) {
					dispatch(addToDummyChat({ comment }));
					return recursiveFeed(idx + 1);
				}

				dispatch(setDummyChat({ comments: [] }));
				return recursiveFeed(0);
			}, time);
		};

		recursiveFeed(0);

		return () => {
			if (timer.current) clearTimeout(timer.current);
		};

		// remove everything except for event id or we'll get infinite loops
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, eventId]);
};

export default useSetDummyChat;
